import { useEffect, useState } from 'react';

import { createClient } from 'contentful';

const CONTENTFUL_SPACE = '0qhqhn3xauc3';
const CONTENTFUL_ACCESS_TOKEN = '0f243ce9285ce061d169b5d337e547755168c3972d67dc957df8362b504ccdfd';
const RECIPE_CONTENT_TYPE_ID = 'recipe';

const contentfulClient = createClient({
  accessToken: CONTENTFUL_ACCESS_TOKEN,
  space: CONTENTFUL_SPACE,
});

export interface ContentfulRecipeTemplate {
  title: string;
  template: string;
  description?: string;
  connectors?: Array<{
    fields: {
      providerType: string;
    };
  }>;
  featuredRecipe?: boolean;
}

interface UseContentfulRecipeTemplatesResult {
  templates: ContentfulRecipeTemplate[];
  isLoading: boolean;
  error: Error | null;
  getSuggestedTemplates: (sourceType?: string, destType?: string) => ContentfulRecipeTemplate[];
}

/**
 * Fetches recipe templates from Contentful - matches dataProtect newTransfomerIntg.js
 * - Fetches all recipe templates
 * - Provides getSuggestedTemplates to filter by source/dest connectors
 * - Only shows templates marked as featuredRecipe
 */
export const useContentfulRecipeTemplates = (): UseContentfulRecipeTemplatesResult => {
  const [templates, setTemplates] = useState<ContentfulRecipeTemplate[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    let cancelled = false;

    contentfulClient
      .getEntries({
        include: 3,
        content_type: RECIPE_CONTENT_TYPE_ID,
        order: 'fields.title',
      })
      .then((entries) => {
        if (!cancelled) {
          const items: ContentfulRecipeTemplate[] = [];

          for (const entry of entries.items) {
            const fields = entry.fields as unknown as ContentfulRecipeTemplate;

            if (fields.template) {
              items.push({
                title: fields.title || 'Untitled',
                template: fields.template.replace(/```\n/g, '').replace(/\n```/g, ''),
                description: fields.description,
                connectors: fields.connectors,
                featuredRecipe: fields.featuredRecipe,
              });
            }
          }

          setTemplates(items);
        }
      })
      .catch((err) => {
        if (!cancelled) {
          setError(err instanceof Error ? err : new Error(String(err)));
          setTemplates([]);
        }
      })
      .finally(() => {
        if (!cancelled) {
          setIsLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, []);

  const getSuggestedTemplates = (
    sourceType?: string,
    destType?: string
  ): ContentfulRecipeTemplate[] => {
    if (!sourceType && !destType) {
      return [];
    }

    return templates.filter((template) => {
      if (!template.featuredRecipe || !template.connectors) {
        return false;
      }

      let matchesSource = false;
      let matchesDest = false;

      for (const connector of template.connectors) {
        const connectorType = connector.fields.providerType;

        if (sourceType && connectorType === sourceType) {
          matchesSource = true;
        }
        if (destType && connectorType === destType) {
          matchesDest = true;
        }
      }

      // Match both source and dest if both provided
      if (sourceType && destType) {
        return matchesSource && matchesDest;
      }

      // Match either source or dest if only one provided
      return matchesSource || matchesDest;
    });
  };

  return { templates, isLoading, error, getSuggestedTemplates };
};
